import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Trash2, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtTime } from "@/lib/sourceMeta";
import { BriefView } from "@/components/BriefView";

export function DigestPage() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/digests/${id}`).then((r) => setD(r.data)).catch(() => setD(false)); }, [id]);
  return (
    <main className="mx-auto max-w-6xl px-4 sm:px-6 py-8 sm:py-12 space-y-6">
      <Link to="/history" data-testid="back-to-history" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900"><ArrowLeft className="w-4 h-4" /> All briefs</Link>
      <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">{d ? fmtTime(d.created_at) : ""}</p>
      {d === null ? <div className="h-64 rounded-3xl bg-surface/70 animate-pulse" /> : d === false ? <p>Not found.</p> :
        d.status === "done" ? <BriefView digest={d} /> : <p className="text-slate-600">This brief {d.status === "running" ? "is still being written" : `failed: ${d.error}`}.</p>}
    </main>
  );
}

export default function History() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/digests").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const del = async (id) => {
    if (!window.confirm("Delete this brief?")) return;
    await api.delete(`/digests/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <main data-testid="history-page" className="mx-auto max-w-4xl px-4 sm:px-6 py-8 sm:py-12">
      <h1 className="font-display text-4xl sm:text-5xl text-slate-900">Past briefs</h1>
      <p className="mt-2 text-slate-600">Every summary Daybreyk has written for you.</p>
      <ul className="mt-8 space-y-3">
        {items === null && <li className="h-20 rounded-2xl bg-surface/70 animate-pulse" />}
        {items?.length === 0 && <li data-testid="history-empty" className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-500">No briefs yet.</li>}
        {(items ?? []).map((d) => (
          <li key={d.digest_id} data-testid={`history-item-${d.digest_id}`} className="flex items-center gap-3 rounded-2xl bg-surface border border-slate-200 p-4 sm:p-5 hover:border-teal-400 transition-colors">
            <Link to={`/digest/${d.digest_id}`} className="flex-1 min-w-0">
              <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{fmtTime(d.created_at)} · {d.trigger === "scheduled" ? "auto" : "manual"}</p>
              <p className="mt-1 font-semibold text-slate-900 truncate">{d.status === "done" ? d.result?.headline ?? "Brief" : d.status === "running" ? "Writing…" : "Failed"}</p>
            </Link>
            <button data-testid={`history-delete-${d.digest_id}`} onClick={() => del(d.digest_id)} aria-label="Delete" className="grid place-items-center w-9 h-9 rounded-xl border border-slate-200 text-slate-500 hover:bg-rose-600 hover:text-white transition-colors"><Trash2 className="w-4 h-4" /></button>
            <ChevronRight className="w-5 h-5 text-slate-400 hidden sm:block" />
          </li>
        ))}
      </ul>
    </main>
  );
}
